import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, Plus, Calendar, Loader2, Play, Pause,
  ChevronRight, Edit2, Trash2, Briefcase, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { useTranslation } from 'react-i18next';
import { timeTrackingApi } from '../../services';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface TimeEntry {
  id: string;
  case_id: string;
  case_name?: string;
  hours: number;
  description: string;
  date: string;
  hourly_rate: number;
  billable: boolean;
  created_at?: string;
}

interface Case {
  id: string;
  topic: string;
}

interface TimeTrackingViewProps {
  cases: Case[];
  user: any;
  onRefresh: () => void;
}

const TimeTrackingViewMinimal: React.FC<TimeTrackingViewProps> = ({ cases, user, onRefresh }) => {
  const { t, i18n } = useTranslation('timeTracking');
  const currentLanguage = i18n.language?.split('-')[0] || 'en';
  
  // Get lawyer's default hourly rate from user settings
  const defaultHourlyRate = user?.settings?.default_hourly_rate || 2500;
  
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerCaseId, setTimerCaseId] = useState('');
  
  const [formData, setFormData] = useState({
    case_id: '',
    hours: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    hourly_rate: defaultHourlyRate.toString(),
    billable: true
  });

  useEffect(() => {
    fetchEntries();
  }, []);

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isTimerRunning) {
      interval = setInterval(() => {
        setTimerSeconds(s => s + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning]);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/lexy/all-time-entries`, {
        headers: { Authorization: `Bearer ${auth.getToken()}` }
      });
      setEntries(response.data?.entries || []);
    } catch (error) {
      console.error('Error fetching time entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        hours: parseFloat(formData.hours) || 0,
        hourly_rate: parseFloat(formData.hourly_rate) || defaultHourlyRate
      };

      if (editingEntry) {
        await axios.put(`${API}/lexy/time-entries/${editingEntry.id}`, payload, {
          headers: { Authorization: `Bearer ${auth.getToken()}` }
        });
        toast.success(t('entryUpdated'));
      } else {
        await axios.post(`${API}/lexy/time-entries`, payload, {
          headers: { Authorization: `Bearer ${auth.getToken()}` }
        });
        toast.success(t('timeRegistered'));
      }

      setShowForm(false);
      setEditingEntry(null);
      resetForm();
      fetchEntries();
    } catch (error) {
      toast.error(t('couldNotSave'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('confirmDelete'))) return;
    try {
      await axios.delete(`${API}/lexy/time-entries/${id}`, {
        headers: { Authorization: `Bearer ${auth.getToken()}` }
      });
      toast.success(t('deleted'));
      fetchEntries();
    } catch (error) {
      toast.error(t('couldNotDelete'));
    }
  };

  const resetForm = () => {
    setFormData({
      case_id: '',
      hours: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      hourly_rate: defaultHourlyRate.toString(),
      billable: true
    });
  };

  const startTimer = (caseId: string) => {
    setTimerCaseId(caseId);
    setTimerSeconds(0);
    setIsTimerRunning(true);
  };

  const stopTimer = () => {
    setIsTimerRunning(false);
    const hours = timerSeconds / 3600;
    setFormData(prev => ({
      ...prev,
      case_id: timerCaseId,
      hours: hours.toFixed(2)
    }));
    setShowForm(true);
    setTimerSeconds(0);
  };

  const formatTimer = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Stats - always use lawyer's default hourly rate for calculations
  const stats = useMemo(() => {
    const totalHours = entries.reduce((sum, e) => sum + (e.hours || 0), 0);
    // Use lawyer's default hourly rate, not stored rate in entries
    const totalValue = totalHours * defaultHourlyRate;
    const billableHours = entries.filter(e => e.billable).reduce((sum, e) => sum + (e.hours || 0), 0);
    const thisWeek = entries.filter(e => {
      const entryDate = new Date(e.date);
      const now = new Date();
      const weekAgo = new Date(now.setDate(now.getDate() - 7));
      return entryDate >= weekAgo;
    }).reduce((sum, e) => sum + (e.hours || 0), 0);
    return { totalHours, totalValue, billableHours, thisWeek };
  }, [entries, defaultHourlyRate]);

  const getCaseName = (caseId: string) => {
    return cases.find(c => c.id === caseId)?.topic || t('unknownCase');
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const diffDays = Math.floor((today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return t('today');
    if (diffDays === 1) return t('yesterday');
    return date.toLocaleDateString(currentLanguage === 'en' ? 'en-US' : 'nb-NO', { day: 'numeric', month: 'short' });
  };

  const formatCurrency = (value: number) => {
    if (currentLanguage === 'en') {
      return `$${value.toLocaleString('en-US')}`;
    }
    return `${value.toLocaleString('nb-NO')} kr`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto px-8 py-12">
        
        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">{t('title')}</h1>
            <div className="flex items-center gap-3">
              {/* Timer */}
              {isTimerRunning ? (
                <button
                  onClick={stopTimer}
                  className="flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-medium rounded-lg transition-colors"
                >
                  <Pause className="w-4 h-4" />
                  {formatTimer(timerSeconds)}
                </button>
              ) : (
                <div className="relative group">
                  <button type="button" className="flex items-center gap-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 text-sm font-medium rounded-lg transition-colors">
                    <Play className="w-4 h-4" />
                    {t('startTimer')}
                  </button>
                  <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    <div className="p-2">
                      <p className="text-xs text-gray-500 px-3 py-2">{t('selectCase')}</p>
                      {cases.slice(0, 5).map(c => (
                        <button
                          key={c.id}
                          onClick={() => startTimer(c.id)}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md truncate"
                        >
                          {c.topic}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              <button
                onClick={() => { resetForm(); setEditingEntry(null); setShowForm(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                {t('newEntry')}
              </button>
            </div>
          </div>
          <p className="text-gray-500 text-sm">{t('subtitle')}</p>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-8 mb-8 pb-8 border-b border-gray-100">
          <div>
            <div className="text-3xl font-semibold text-gray-900">{stats.totalHours.toFixed(1)}</div>
            <div className="text-sm text-gray-500">{t('totalHours')}</div>
          </div>
          <div className="w-px h-10 bg-gray-100" />
          <div>
            <div className="text-3xl font-semibold text-gray-900">{stats.thisWeek.toFixed(1)}</div>
            <div className="text-sm text-gray-500">{t('thisWeek')}</div>
          </div>
          <div className="w-px h-10 bg-gray-100" />
          <div>
            <div className="text-3xl font-semibold text-gray-900">{stats.billableHours.toFixed(1)}</div>
            <div className="text-sm text-gray-500">{t('billable')}</div>
          </div>
          <div className="w-px h-10 bg-gray-100" />
          <div>
            <div className="text-3xl font-semibold text-gray-900">{formatCurrency(stats.totalValue)}</div>
            <div className="text-sm text-gray-500">{t('value')}</div>
          </div>
        </div>

        {/* Time entries list */}
        <div className="space-y-1">
          {entries.length === 0 ? (
            <div className="py-20 text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-6 h-6 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">{t('noEntries')}</h3>
              <p className="text-gray-500 text-sm mb-6">{t('noEntriesDescription')}</p>
              <button
                onClick={() => { resetForm(); setShowForm(true); }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
              >
                <Plus className="w-4 h-4" />
                {t('newEntry')}
              </button>
            </div>
          ) : (
            entries.map((entry) => (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="group flex items-center gap-4 px-4 py-3 rounded-lg hover:bg-gray-50 transition-all"
              >
                {/* Hours */}
                <div className="w-16 text-center">
                  <div className="text-lg font-semibold text-gray-900">{(entry.hours || 0).toFixed(1)}</div>
                  <div className="text-xs text-gray-400">{t('hours')}</div>
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-gray-900 truncate">{entry.description}</h3>
                    {entry.billable && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">{t('billableLabel')}</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{getCaseName(entry.case_id)}</p>
                </div>
                
                {/* Meta - use lawyer's default hourly rate for value calculation */}
                <div className="flex items-center gap-6 text-sm text-gray-400">
                  <span>{formatDate(entry.date)}</span>
                  <span className="w-24 text-right font-medium text-gray-900">
                    {formatCurrency((entry.hours || 0) * defaultHourlyRate)}
                  </span>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => {
                      setEditingEntry(entry);
                      setFormData({
                        case_id: entry.case_id,
                        hours: entry.hours.toString(),
                        description: entry.description,
                        date: entry.date,
                        hourly_rate: entry.hourly_rate.toString(),
                        billable: entry.billable
                      });
                      setShowForm(true);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <Edit2 className="w-4 h-4 text-gray-400" />
                  </button>
                  <button
                    onClick={() => handleDelete(entry.id)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Form modal */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 flex items-center justify-center z-50"
              onClick={() => setShowForm(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {editingEntry ? t('editEntry') : t('newTimeEntry')}
                  </h2>
                  <button type="button" onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('case')}</label>
                    <select
                      value={formData.case_id}
                      onChange={(e) => setFormData({ ...formData, case_id: e.target.value })}
                      required
                      className="w-full px-3 py-2 bg-gray-50 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                    >
                      <option value="">{t('selectCase')}</option>
                      {cases.map(c => (
                        <option key={c.id} value={c.id}>{c.topic}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('hoursLabel')}</label>
                      <input
                        type="number"
                        step="0.25"
                        value={formData.hours}
                        onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                        required
                        placeholder="0.0"
                        className="w-full px-3 py-2 bg-gray-50 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('date')}</label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        required
                        className="w-full px-3 py-2 bg-gray-50 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('description')}</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      rows={3}
                      placeholder={t('descriptionPlaceholder')}
                      className="w-full px-3 py-2 bg-gray-50 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 resize-none"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('hourlyRate')}</label>
                      <input
                        type="number"
                        value={formData.hourly_rate}
                        onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-50 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.billable}
                          onChange={(e) => setFormData({ ...formData, billable: e.target.checked })}
                          className="w-4 h-4 rounded text-gray-900 focus:ring-gray-200"
                        />
                        <span className="text-sm text-gray-700">{t('billableLabel')}</span>
                      </label>
                    </div>
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors"
                    >
                      {t('cancel')}
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      {editingEntry ? t('update') : t('save')}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default TimeTrackingViewMinimal;
